import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState, useRef, lazy, Suspense } from "react";
import {
  Scale, FileText, GraduationCap, BookOpen,
  Gavel, ArrowRight, Sparkles, UserCircle, Languages,
  Zap, Globe, ShieldCheck, Cpu, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Words3D } from "@/components/Words3D";
import { ScalesEmblem } from "@/components/ScalesEmblem";
import { Logo3D } from "@/components/Logo3D";
import { faqSchema, breadcrumbSchema, organizationSchema, softwareApplicationSchema } from "@/lib/seo";

const HeroScales3D = lazy(() => import("@/components/HeroScales3D"));

const HOME_FAQS = [
  { q: "What is JusticeVerse?", a: "JusticeVerse is India's premier AI legal platform offering AI legal research, legal drafting, case-brief analysis, document translation and judiciary exam preparation in 11+ Indian languages." },
  { q: "Who can use JusticeVerse?", a: "Advocates, law firms, law students, judiciary aspirants, judges and individuals seeking trustworthy legal help in India." },
  { q: "Is JusticeVerse free to start?", a: "Yes — you can start free with research messages and free CaseBriefs, then upgrade to a plan from ₹29/month." },
  { q: "Does JusticeVerse cover the new Indian criminal laws?", a: "Yes — it covers BNS, BNSS and the Bharatiya Sakshya Adhiniyam along with the IPC, CrPC, CPC and the Constitution." },
];

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/e8e2ab20-531a-4e8a-8ea0-d87bf94da2cc/images/445c81c745ca3d176712e81188522531aa2f68336af5096d26ba9f3dbd7103f5.png";

const SIMPLIFY_BANNER =
  "https://static.prod-images.emergentagent.com/jobs/e8e2ab20-531a-4e8a-8ea0-d87bf94da2cc/images/469b96091ff746b4262d642824a4d5b411a333e60cf780a74aa1f164abd3df0d.png";

const STATS = [
  { value: "11+", label: "Languages Supported" },
  { value: "8", label: "AI Workspaces" },
  { value: "24/7", label: "Research Assistant" },
];

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const FEATURES = [
  { icon: Cpu, title: "AI-Powered", sub: "Advanced legal intelligence" },
  { icon: Globe, title: "360° Legal Context", sub: "BNS · BNSS · IPC · CrPC" },
  { icon: ShieldCheck, title: "Verified Sources", sub: "Cited, never fabricated" },
  { icon: Zap, title: "Enterprise Grade", sub: "Secure & device-bound" },
];

// Suite cards (subset of MODULES, in showcase order)
const SUITE = [
  { icon: Sparkles, title: "JusticeVerse AI", tag: "Research & Consultation", desc: "Conversational legal researcher with citations in 11 languages.", to: "/justicebot" },
  { icon: Gavel, title: "Counsel AI", tag: "Drafting", desc: "Bail applications, writs, plaints, NDAs & agreements — export to Word.", to: "/counsel" },
  { icon: FileText, title: "CaseBrief AI", tag: "Analysis", desc: "Facts, issues, ratio decidendi, timeline & citations in seconds.", to: "/casebrief" },
  { icon: Languages, title: "Verse Translation", tag: "Translation", desc: "Translate scanned legal docs into any language, format preserved.", to: "/verse-translation" },
  { icon: GraduationCap, title: "PrepMate AI", tag: "Judiciary Prep", desc: "Study plans, mock tests, PYQs, viva practice & analytics.", to: "/prepmate" },
  { icon: BookOpen, title: "Articles & Daily Digest", tag: "Library", desc: "Vetted articles, judgments & an AI-summarised legal news digest.", to: "/knowledge-hub" },
];

// Build-your-workspace options
const PATHS = [
  { icon: Sparkles, label: "Legal Research", to: "/justicebot" },
  { icon: Gavel, label: "Drafting", to: "/counsel" },
  { icon: FileText, label: "Case Analysis", to: "/casebrief" },
  { icon: Languages, label: "Translation", to: "/verse-translation" },
  { icon: GraduationCap, label: "Judiciary Prep", to: "/prepmate" },
];

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    api.get("/news?limit=2").then(({ data }) => setNews(data)).catch(() => {});
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="JusticeVerse – Premier AI Legal Research, Drafting & Judiciary Preparation Platform"
        schema={[
          organizationSchema(),
          softwareApplicationSchema(),
          faqSchema(HOME_FAQS),
          breadcrumbSchema([{ name: "Home", path: "/" }]),
        ]}
      />
      {/* HERO */}
      <section className="force-dark relative min-h-[88vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-canvas/80" />
        <div className="absolute inset-0 gold-glow" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-28 w-full">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-6 items-center">
            <div>
              <motion.div initial="hidden" animate="show" variants={fade} custom={0}>
                <span className="inline-flex items-center gap-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gold border border-gold/30 rounded-full px-4 py-1.5">
                  <Scale className="w-3.5 h-3.5" /> India&apos;s Premier Legal AI Workspace
                </span>
              </motion.div>
              <h1 className="font-serif text-5xl sm:text-7xl lg:text-7xl xl:text-8xl font-light leading-[1.04] tracking-tight text-ink mt-8">
                <motion.span
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                  className="block text-2xl sm:text-3xl lg:text-4xl text-gold tracking-normal mb-4 not-italic"
                >
                  JusticeVerse
                </motion.span>
                <Words3D
                  text="Democratizing Law Through Technology"
                  goldWords={[3]} italicWords={[3]} delay={0.35}
                  className="block"
                />
              </h1>
              <motion.p
                initial="hidden" animate="show" variants={fade} custom={5}
                className="text-inkmuted text-base sm:text-lg leading-relaxed mt-8 max-w-2xl"
              >
                India&apos;s most advanced AI legal workspace for lawyers, judges, students, researchers and
                law firms.
              </motion.p>
              <motion.div
                initial="hidden" animate="show" variants={fade} custom={6}
                className="flex flex-col sm:flex-row gap-4 mt-12"
              >
                <Link
                  to="/justicebot"
                  data-testid="hero-justicebot-btn"
                  className="group rounded-full bg-btn text-btnink hover:bg-btnhover px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                >
                  Consult JusticeVerse AI
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/careers"
                  data-testid="hero-careers-btn"
                  className="rounded-full bg-transparent border border-gold text-gold hover:bg-gold/10 px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all flex items-center justify-center"
                >
                  Explore Careers
                </Link>
                {user && (
                  <Link
                    to="/dashboard"
                    data-testid="hero-profile-btn"
                    className="rounded-full bg-transparent border border-hairline text-ink hover:bg-surface2 px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                  >
                    <UserCircle className="w-4 h-4" /> My Profile
                  </Link>
                )}
              </motion.div>
            </div>

            {/* 3D Scales of Justice (lighter static emblem on mobile) */}
            <div className="relative h-[320px] sm:h-[420px] lg:h-[540px] flex items-center justify-center" data-testid="hero-3d-wrap">
              {isMobile
                ? <ScalesEmblem className="h-full" />
                : <Suspense fallback={<ScalesEmblem className="h-full" />}><HeroScales3D /></Suspense>}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="border-y border-hairline bg-canvas/50 backdrop-blur-md relative z-20">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-hairline">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="p-7 sm:p-9 flex flex-col items-center text-center group cursor-default hover:bg-surface transition-colors"
                data-testid="feature-strip-item"
              >
                <Icon className="w-7 h-7 mb-4 text-ink/50 group-hover:text-gold transition-colors" strokeWidth={1.25} />
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink/80">{f.title}</span>
                <span className="text-xs text-inkfaint mt-1.5">{f.sub}</span>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* SIGNATURE AI SUITE — horizontal scroll */}
      <section className="py-24 sm:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-end justify-between mb-12">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">The Suite</span>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-ink mt-4 tracking-tight">Signature AI suite</h2>
          </div>
          <span className="hidden sm:flex font-mono text-[10px] uppercase tracking-[0.2em] text-inkfaint items-center gap-2">
            Scroll <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
        <div className="flex overflow-x-auto gap-5 px-5 sm:px-8 pb-4 snap-x snap-mandatory hide-scrollbar">
          {SUITE.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="snap-center shrink-0"
              >
                <Link
                  to={m.to}
                  data-testid="signature-product-card"
                  className="group block w-[280px] sm:w-[360px] h-[440px] rounded-3xl glass-card p-8 flex flex-col justify-end relative overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-gold/50"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                       style={{ background: "radial-gradient(circle at 50% 20%, rgb(var(--c-gold) / 0.14), transparent 60%)" }} />
                  <div className="absolute top-8 left-8">
                    <Icon className="w-10 h-10 text-gold" strokeWidth={1.25} />
                  </div>
                  <div className="relative">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{m.tag}</span>
                    <h3 className="font-serif text-3xl text-ink mt-3">{m.title}</h3>
                    <p className="text-sm text-inkmuted leading-relaxed mt-3">{m.desc}</p>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink mt-6 flex items-center gap-2 group-hover:text-gold transition-colors">
                      Enter <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-hairline bg-surface">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-3 divide-x divide-hairline">
          {STATS.map((s) => (
            <div key={s.label} className="py-10 px-4 text-center">
              <div className="font-serif text-4xl sm:text-5xl text-ink">{s.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkfaint mt-3">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BUILD YOUR WORKSPACE */}
      <section className="py-24 sm:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-7xl mx-auto px-5 sm:px-8">
          {/* Visualizer */}
          <motion.div {...reveal} className="order-2 lg:order-1 flex items-center justify-center">
            <div className="relative aspect-square w-full max-w-md rounded-full border border-hairline flex items-center justify-center overflow-hidden bg-gradient-to-br from-canvas to-surface"
                 style={{ boxShadow: "inset 0 0 120px rgb(var(--c-gold) / 0.12)" }}>
              <div className="absolute w-56 h-56 rounded-full blur-3xl opacity-30" style={{ background: "rgb(var(--c-gold))" }} />
              <Logo3D />
              {/* rotating ring */}
              <motion.div
                className="absolute w-[88%] h-[88%] rounded-full border border-dashed border-gold/25"
                animate={{ rotate: 360 }} transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </motion.div>
          {/* Controls */}
          <motion.div {...reveal} className="order-1 lg:order-2">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Get Started</span>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-ink mt-4 tracking-tight">Build your workspace</h2>
            <p className="text-inkmuted mt-4 max-w-md">Choose where to begin. Your AI legal workspace adapts to how you practise.</p>
            <div className="flex flex-col gap-3 mt-8">
              {PATHS.map((p) => {
                const Icon = p.icon;
                return (
                  <button
                    key={p.label}
                    onClick={() => navigate(p.to)}
                    data-testid={`workspace-open-${p.to.replace("/", "")}`}
                    className="group flex items-center justify-between rounded-xl border border-hairline px-5 py-4 text-left transition-all hover:bg-gold/10 hover:border-gold"
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-inkmuted group-hover:text-gold transition-colors" strokeWidth={1.5} />
                      <span className="text-sm font-medium text-inkmuted group-hover:text-ink transition-colors">{p.label}</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-inkfaint group-hover:text-gold group-hover:translate-x-0.5 transition-all" />
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SIMPLIFY — CTA BANNER */}
      <section className="relative overflow-hidden" data-testid="simplify-cta-section">
        <div
          className="relative bg-cover bg-center min-h-[540px] sm:min-h-[660px] lg:min-h-[780px] flex flex-col items-center"
          style={{ backgroundImage: `url(${SIMPLIFY_BANNER})` }}
        >
          <div className="absolute inset-x-0 top-0 h-2/3 bg-gradient-to-b from-white/55 via-white/15 to-transparent pointer-events-none" />
          <motion.div {...reveal} className="relative z-10 text-center px-6 pt-14 sm:pt-20 max-w-3xl">
            <h2 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-[#15233f] drop-shadow-sm">
              Simplify your legal chaos.
            </h2>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-[#1b2c4c] font-medium max-w-xl mx-auto leading-relaxed">
              Research smarter, draft faster, and manage every legal task with JusticeVerse AI-powered precision.
            </p>
            <Link
              to={user ? "/justicebot" : "/register"}
              data-testid="simplify-start-trial-btn"
              className="group inline-flex items-center gap-2 mt-8 sm:mt-10 rounded-xl bg-[#15233f] text-white px-9 py-4 text-sm font-semibold tracking-wide transition-all hover:scale-[1.03] hover:bg-[#1f3258] active:scale-95 shadow-xl shadow-[#15233f]/30"
            >
              Start free trial
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* NEWS PREVIEW */}
      {news.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-serif text-3xl sm:text-4xl font-light text-ink">Latest Legal News</h2>
            <Link to="/knowledge-hub" className="font-mono text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-2">
              All Updates <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {news.map((n) => (
              <div key={n.id} className="glass-card rounded-2xl p-8" data-testid="home-news-card">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] bg-surface border border-hairline text-inkmuted px-3 py-1 rounded-full">{n.category}</span>
                  <span className="font-mono text-[11px] text-inkfaint">{n.date}</span>
                </div>
                <h3 className="font-serif text-2xl text-ink">{n.title}</h3>
                <p className="text-sm text-inkmuted leading-relaxed mt-3">{n.summary}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CLOSING CTA */}
      <section className="force-dark py-28 sm:py-36 flex flex-col items-center justify-center text-center px-6 border-t border-hairline relative overflow-hidden">
        <div className="absolute inset-0 gold-glow opacity-40" />
        <motion.div {...reveal} className="relative z-10 flex flex-col items-center">
          <span className="font-mono text-gold tracking-[0.3em] text-xs sm:text-sm uppercase mb-6">One verdict and you&apos;re hooked</span>
          <h2 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-light text-ink mb-10 tracking-tight max-w-4xl leading-[1.05]">
            Experience the future of law
          </h2>
          <Link
            to={user ? "/justicebot" : "/register"}
            data-testid="closing-cta-button"
            className="group rounded-full bg-gold text-black px-12 py-5 font-mono text-xs sm:text-sm tracking-[0.2em] uppercase transition-all hover:scale-[1.03] active:scale-95 flex items-center gap-3"
          >
            {user ? "Open JusticeVerse AI" : "Start Your Journey"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
